/**
 * Pure display + grouping helpers for the groceries screens. No I/O, so the
 * fiddly parts (department grouping in particular) can be unit-tested on
 * their own — mirrors `lib/plans.ts` / `lib/pantryDisplay.ts`.
 */
import {
  convert,
  departmentOf,
  formatQuantity,
  type Department,
  type ItemLocation,
} from '@fewerbags/core';
import type {
  ExpirationBasis,
  GroceryEvent,
  GroceryItemDto,
  GroceryListDetail,
  GroceryListDto,
  StoreDto,
  UnconvertedReason,
  GroceryLocationSource,
} from '@fewerbags/schema';

export type GroceryListStatus = GroceryListDto['status'];

export const STATUS_LABEL: Record<GroceryListStatus, string> = {
  open: 'Open',
  shopping: 'Shopping',
  done: 'Done',
  archived: 'Archived',
};

export const STATUS_ORDER: readonly GroceryListStatus[] = ['open', 'shopping', 'done', 'archived'];

/** Badge styling per status — "done" is the only fully-complete state. */
export function statusBadgeClass(status: GroceryListStatus): string {
  switch (status) {
    case 'done':
      return 'bg-accent-soft text-accent-ink';
    case 'shopping':
      return 'bg-brand-soft text-brand';
    case 'archived':
      return 'bg-cream text-muted';
    case 'open':
    default:
      return 'bg-cream text-muted';
  }
}

export const DEPARTMENT_LABEL: Record<Department, string> = {
  produce: 'Produce',
  deli: 'Deli',
  bakery: 'Bakery',
  butcher: 'Butcher',
  seafood: 'Seafood',
  aisles: 'Aisles',
  dairy: 'Dairy',
  frozen: 'Frozen',
  other: 'Other',
};

/** Bucket for items whose department can't be determined from location/category. */
export const UNSORTED_KEY = 'unsorted';
export const UNSORTED_LABEL = 'Not yet placed';

export interface DepartmentGroup {
  key: Department | typeof UNSORTED_KEY;
  label: string;
  items: GroceryItemDto[];
}

/** Tooltip naming what a greyed location suggestion is based on (PLAN §4.5). */
export function suggestionBasis(source: GroceryLocationSource | undefined): string {
  switch (source) {
    case 'same_chain':
      return 'Suggested from another store in this chain';
    case 'department':
      return 'Suggested from where other stores keep it';
    case 'category':
      return 'Suggested from the usual department for this kind of item';
    default:
      return 'Not confirmed at this store yet';
  }
}

/** `groceryItemDtoSchema.location` (with `suggested`) → the plain `ItemLocation` `departmentOf` expects. */
function toItemLocation(location: GroceryItemDto['location']): ItemLocation | null {
  if (!location) return null;
  return {
    type: location.type,
    value: location.value,
    ...(location.department ? { department: location.department } : {}),
  };
}

function bySortKey(a: GroceryItemDto, b: GroceryItemDto): number {
  if (a.sortKey !== b.sortKey) return a.sortKey < b.sortKey ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Unchecked items only, grouped by department in walking order. The server's
 * `sortKey` already encodes `(departmentRank, location, name)` (PLAN §4.5), so
 * sorting by it and splitting on department changes reproduces the store's
 * walking order without re-deriving it — `departmentOf` (the same function the
 * server uses) just labels each contiguous run.
 */
export function groupByDepartment(items: readonly GroceryItemDto[]): DepartmentGroup[] {
  const sorted = [...items].sort(bySortKey);
  const groups: DepartmentGroup[] = [];
  for (const item of sorted) {
    const location = toItemLocation(item.location);
    const department = departmentOf(
      { ingredientId: item.ingredientId, category: null, location },
      location,
    );
    const key = department ?? UNSORTED_KEY;
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({
        key,
        label: department ? DEPARTMENT_LABEL[department] : UNSORTED_LABEL,
        items: [item],
      });
    }
  }
  return groups;
}

/** Checked items, most recently checked first, for the collapsed "In cart" section. */
export function sortCheckedItems(items: readonly GroceryItemDto[]): GroceryItemDto[] {
  return [...items].sort((a, b) => {
    const at = a.checkedAt ?? '';
    const bt = b.checkedAt ?? '';
    if (at !== bt) return at < bt ? 1 : -1;
    return bySortKey(a, b);
  });
}

/** Checked off *or* resolved from home ("I have this", D35) — both count as done. */
export function isChecked(item: GroceryItemDto): boolean {
  return item.checkedAt !== null;
}

/** Resolved by "I have this" (D35) rather than a purchase. */
export function isHave(item: Pick<GroceryItemDto, 'haveAt'>): boolean {
  return item.haveAt != null;
}

/**
 * How much "I have this" records in the pantry: what the meals need, or the
 * buy amount when there is no separate need (mirrors the API's rule).
 */
export function haveQty(item: Pick<GroceryItemDto, 'neededQty' | 'buyQty'>): number {
  return item.neededQty !== null && item.neededQty > 0 ? item.neededQty : item.buyQty;
}

/** "15 oz can" from "black beans, 15 oz can": the label without the ingredient name. */
function packageText(label: string, ingredientName: string): string {
  const prefix = `${ingredientName}, `;
  return label.toLowerCase().startsWith(prefix.toLowerCase()) ? label.slice(prefix.length) : label;
}

/** Whole packages in `buyQty`, or null when the package size is unknown or does not divide it. */
function packageCount(
  item: Pick<GroceryItemDto, 'buyQty' | 'unit' | 'packageQty' | 'packageUnit'>,
): number | null {
  if (!item.packageQty || !item.packageUnit) return null;
  let size: number;
  try {
    size = convert(item.packageQty, item.packageUnit, item.unit);
  } catch {
    return null;
  }
  if (!(size > 0)) return null;
  const count = item.buyQty / size;
  const rounded = Math.round(count);
  return rounded >= 1 && Math.abs(count - rounded) < 1e-3 ? rounded : null;
}

/**
 * F5 "need vs buy": "Need 6 oz · buy 1 × 15 oz can" when what the meals need
 * differs from what is bought (package rounding, or an edited amount). Null
 * when there is no separate need or the two read the same.
 */
export function needVsBuyLabel(
  item: Pick<
    GroceryItemDto,
    | 'neededQty'
    | 'buyQty'
    | 'unit'
    | 'ingredientName'
    | 'productLabel'
    | 'packageQty'
    | 'packageUnit'
  >,
): string | null {
  if (item.neededQty === null) return null;
  const need = formatQuantity(item.neededQty, item.unit);
  const buy = formatQuantity(item.buyQty, item.unit);
  if (need === buy) return null;
  if (item.productLabel) {
    const pkg = packageText(item.productLabel, item.ingredientName);
    const count = packageCount(item);
    return count !== null
      ? `Need ${need} · buy ${count} × ${pkg}`
      : `Need ${need} · buy ${buy} (${pkg})`;
  }
  return `Need ${need} · buy ${buy}`;
}

/** "$3.99" from cents, or null when the price isn't known. */
export function formatPriceCents(priceCents: number | null): string | null {
  if (priceCents === null) return null;
  return `$${(priceCents / 100).toFixed(2)}`;
}

/** Dollars-and-cents text input → integer cents, or null for blank/invalid input. */
export function parsePriceInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

/** Integer cents → the text a price input should show, or "" when unset. */
export function priceInputValue(priceCents: number | null): string {
  return priceCents === null ? '' : (priceCents / 100).toFixed(2);
}

/** "aisle 12", "endcap 3 · Produce" for a store location. */
export function locationLabel(location: { type: string; value: string } | null): string | null {
  if (!location) return null;
  if (location.type === 'department') return location.value;
  return `${location.type} ${location.value}`;
}

/** Why a generated line is marked "unconverted" — the badge's tooltip text. */
export const UNCONVERTED_HINT: Record<UnconvertedReason, string> = {
  missing_density:
    'Weights and volumes for this ingredient could not be combined. Add its density on the ingredient page.',
  missing_unit_weight:
    'Counts and weights for this ingredient could not be combined. Add the average weight of one on the ingredient page.',
  incompatible_units:
    'Some amounts for this ingredient are in units that cannot be combined, so they are listed separately.',
};

/** Tooltip text for an item's unconverted badge, or null when the line converted cleanly. */
export function unconvertedHint(item: Pick<GroceryItemDto, 'unconvertedReason'>): string | null {
  return item.unconvertedReason ? UNCONVERTED_HINT[item.unconvertedReason] : null;
}

/** Short provenance hint shown under the expiration field on the check-off sheet. */
export const EXPIRATION_RULE_LABEL: Record<string, string> = {
  history_store: 'from your last purchase here',
  history_any: 'from your last purchase',
  store_product: "from this store's shelf-life rule for this product",
  store_adjustment: "from this store's shelf-life rule",
  seed: 'from typical shelf life',
  category: 'from typical shelf life for this kind of ingredient',
  unknown: 'no history yet — pick a date',
};

const PRIOR_PHRASE: Record<NonNullable<ExpirationBasis['priorRule']>, string> = {
  store_product: "this store's shelf life for it",
  store_adjustment: "this store's shelf-life rule",
  seed: 'the USDA estimate',
  category: 'the typical shelf life',
};

/**
 * The hint under the expiry field on the check-off sheet. History-based
 * defaults say what they rest on ("Based on 3 of your purchases here and the
 * USDA estimate"); every other rule keeps its short label.
 */
export function expirationHint(rule: string, basis: ExpirationBasis | null | undefined): string {
  if (basis && basis.observations > 0 && (rule === 'history_store' || rule === 'history_any')) {
    const here = rule === 'history_store' ? ' here' : '';
    const purchases =
      basis.observations === 1
        ? `your last purchase${here}`
        : `${basis.observations} of your purchases${here}`;
    const prior = basis.priorRule ? ` and ${PRIOR_PHRASE[basis.priorRule]}` : '';
    return `Based on ${purchases}${prior}`;
  }
  return EXPIRATION_RULE_LABEL[rule] ?? EXPIRATION_RULE_LABEL['unknown']!;
}

/**
 * Whether the shopper changed the suggested expiry. Only changed dates are
 * recorded as observations; an accepted suggestion is not evidence.
 */
export function expiryWasEdited(suggested: string | null | undefined, value: string): boolean {
  return value.trim() !== (suggested ?? '');
}

/** The list to surface on the dashboard: the most recently updated open/shopping list, if any. */
export function mostRecentOpenList(lists: readonly GroceryListDto[]): GroceryListDto | null {
  const candidates = lists.filter((l) => l.status === 'open' || l.status === 'shopping');
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) =>
    a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0,
  )[0]!;
}

export type NearestStoreMatch = StoreDto & { distanceM: number };

/**
 * PLAN §4.7 web preview: decide whether the "switch store" banner should
 * appear — only when the nearest detected store is a different one than the
 * list currently uses. `matches` is nearest-first, per `GET /stores/nearest`.
 */
export function nearestStoreSuggestion(
  matches: readonly NearestStoreMatch[],
  currentStoreId: string | null,
): NearestStoreMatch | null {
  const nearest = matches[0];
  if (!nearest || nearest.id === currentStoreId) return null;
  return nearest;
}

/** "Aug 24, 2026" for a price-history point's timestamp. */
export function formatObservedDate(iso: string): string {
  // Price history is date-only (`YYYY-MM-DD`); read it as a local calendar date
  // so it does not shift a day west of UTC.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Most recent price-history points first, capped for the check-off sheet's compact list. */
export function recentPriceHistory<T extends { observedAt: string }>(
  points: readonly T[],
  limit = 5,
): T[] {
  return [...points]
    .sort((a, b) => (a.observedAt < b.observedAt ? 1 : a.observedAt > b.observedAt ? -1 : 0))
    .slice(0, limit);
}

/**
 * Apply one live-sync event (D14) to a cached list detail. Returns the same
 * reference when the event doesn't touch this list, so callers can pass the
 * result straight to `setQueryData`.
 */
export function applyGroceryEvent(
  detail: GroceryListDetail,
  event: GroceryEvent,
): GroceryListDetail {
  switch (event.type) {
    case 'item.checked':
    case 'item.have':
    case 'item.unchecked':
    case 'item.updated':
      return {
        ...detail,
        items: detail.items.some((i) => i.id === event.item.id)
          ? detail.items.map((i) => (i.id === event.item.id ? event.item : i))
          : [...detail.items, event.item],
      };
    case 'item.added':
      return detail.items.some((i) => i.id === event.item.id)
        ? detail
        : { ...detail, items: [...detail.items, event.item] };
    case 'item.removed':
      return { ...detail, items: detail.items.filter((i) => i.id !== event.itemId) };
    case 'list.updated':
      return { ...detail, list: event.list };
    case 'list.regenerated':
      return event.detail;
    default:
      return detail;
  }
}

/**
 * Which shared values on an item can be reported (PLAN §9 #3c): only a shared
 * consensus location (not a greyed suggestion or the household's own) and a
 * shared median price (not what we paid or our own), at a known store and product.
 */
export function reportableValues(
  item: Pick<GroceryItemDto, 'productId' | 'location' | 'priceCents' | 'priceSource'>,
  storeId: string | null,
): ('price' | 'location')[] {
  if (!storeId || !item.productId) return [];
  const out: ('price' | 'location')[] = [];
  if (item.priceCents !== null && item.priceSource === 'shared') out.push('price');
  const loc = item.location;
  if (loc && !loc.suggested && loc.source === undefined) {
    out.push('location');
  }
  return out;
}

/** True when the household's own price or location for this store is shown. */
export function usesHouseholdOverride(
  item: Pick<GroceryItemDto, 'location' | 'priceSource'>,
): boolean {
  return (
    item.priceSource === 'household_override' || item.location?.source === 'household_override'
  );
}
